import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import css from 'src/components/crm/jobs/job-info/overview/feedbacks/feedbacks.css';
import type {
  FeedbacksPanelProps,
  CANDIDATE_FEEDBACK,
  INTERVIEW_SCHEDULE,
} from 'src/types/crm';
import {getFeedbackTabs} from 'src/components/crm/constant';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {TabList, Tab} from '@spaced-out/ui-design-system/lib/components/Tabs';
import {SubTitleLarge} from '@spaced-out/ui-design-system/lib/components/Text';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {sizeFluid} from '@spaced-out/ui-design-system/lib/styles/variables/_size';
import {
  selectCandidateInterviewSchedules,
  selectCandidateHmExperienceSurveys,
  selectCandidateFeedbacks,
  selectCompletedInterviewSchedules,
  selectCompletedCandidateFeedbacks,
} from 'src/selectors/crm';
import {useSelector, useDispatch} from 'react-redux';
import InterviewFeedback from 'src/components/crm/jobs/job-info/overview/feedbacks/cards/interview-feedback';
import SurveyCard from 'src/components/crm/jobs/job-info/overview/feedbacks/cards/survey-card';
import ApplicationReviewCard from 'src/components/crm/jobs/job-info/overview/feedbacks/cards/application-review-card';
import {CompletedFeedbackTab} from 'src/components/crm/jobs/job-info/overview/feedbacks/completed-feedback-tab/completed-feedback-tab';
import useScrollLoader from 'src/hooks/useScrollLoader';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  getHmExperienceSurveys,
  getCandidateFeedbacks,
  getInterviewSchedules,
} from 'src/action-creators/crm';

// Add type for the tab IDs
type TabId =
  | 'survey'
  | 'applicationReview'
  | 'interviewFeedbacks'
  | 'completed';

const FeedbacksPanel = ({
  isOpen,
  onClose,
  jobId,
  completedData,
  initialTab,
  from = 'DASHBOARD',
}: FeedbacksPanelProps) => {
  const {t} = useLabelTranslation();
  const FEEDBACK_TABS = getFeedbackTabs(t);
  const [selected, setSelected] = React.useState<{
    tabId: TabId;
    label: string;
    // @ts-expect-error - TS2345 - Argument of type '{ tabId: string; label?: string | undefined; }' is not assignable to parameter of type '{ tabId: TabId; label: string; } | (() => { tabId: TabId; label: string; })'.
  }>(initialTab || FEEDBACK_TABS[0]);

  // Add Completed to pagination state
  const [paginationState, setPaginationState] = React.useState({
    survey: {pageNumber: 0, hasMorePages: true},
    applicationReview: {pageNumber: 0, hasMorePages: true},
    interviewFeedbacks: {pageNumber: 0, hasMorePages: true},
    completed: {pageNumber: 0, hasMorePages: false}, // Completed tab doesn't need pagination
  });
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);

  const [accumulatedSurveys, setAccumulatedSurveys] = React.useState([]);
  const [accumulatedReviews, setAccumulatedReviews] = React.useState([]);
  const [accumulatedInterviews, setAccumulatedInterviews] = React.useState([]);

  // Use selectors instead of props
  const surveyData = useSelector(selectCandidateHmExperienceSurveys);
  const applicantReviewData = useSelector(selectCandidateFeedbacks);
  const recentInterviewData = useSelector(selectCandidateInterviewSchedules);
  const dispatch = useDispatch();
  const completedCandidateFeedbacks = useSelector(
    selectCompletedCandidateFeedbacks,
  );

  React.useEffect(() => {
    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'INTERVIEW_SCHEDULE[]'.
    const recentInterviews = (recentInterviewData?.content || []).filter(
      // @ts-expect-error - TS7006 - Parameter 'interview' implicitly has an 'any' type.
      (interview) => {
        const interviewDateTime = new Date(interview.endDate);
        const currentDateTime = new Date();
        return interviewDateTime <= currentDateTime;
      },
    );

    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'HM_EXPERIENCE_SURVEY[]'.
    setAccumulatedSurveys(surveyData?.content || []);
    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'CANDIDATE_FEEDBACK[]'.
    setAccumulatedReviews(applicantReviewData?.content || []);
    setAccumulatedInterviews(recentInterviews);
  }, []);

  React.useEffect(() => {
    setAccumulatedSurveys(
      // @ts-expect-error - TS2345 - Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
      [
        ...new Set(
          // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'HM_EXPERIENCE_SURVEY[]'.
          [...accumulatedSurveys, ...(surveyData?.content || [])].map(
            (survey) => survey.id,
          ),
        ),
      ].map((id) =>
        // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'HM_EXPERIENCE_SURVEY[]'.
        [...accumulatedSurveys, ...(surveyData?.content || [])].find(
          (survey) => survey.id === id,
        ),
      ),
    );
    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'HM_EXPERIENCE_SURVEY[]'.
  }, [surveyData?.content]);

  React.useEffect(() => {
    setAccumulatedReviews(
      // @ts-expect-error - TS2345 - Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
      [
        ...new Set(
          // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'CANDIDATE_FEEDBACK[]'.
          [...accumulatedReviews, ...(applicantReviewData?.content || [])].map(
            (review) => review.id,
          ),
        ),
      ].map((id) =>
        // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'CANDIDATE_FEEDBACK[]'.
        [...accumulatedReviews, ...(applicantReviewData?.content || [])].find(
          (review) => review.id === id,
        ),
      ),
    );
    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'CANDIDATE_FEEDBACK[]'.
  }, [applicantReviewData?.content]);

  React.useEffect(() => {
    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'INTERVIEW_SCHEDULE[]'.
    const recentInterviews = (recentInterviewData?.content || []).filter(
      // @ts-expect-error - TS7006 - Parameter 'interview' implicitly has an 'any' type.
      (interview) => {
        const interviewDateTime = new Date(interview.endDate);
        const currentDateTime = new Date();
        return interviewDateTime <= currentDateTime;
      },
    );
    setAccumulatedInterviews(
      // @ts-expect-error - TS2345 - Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
      [
        ...new Set(
          [...accumulatedInterviews, ...recentInterviews].map(
            (interview) => interview.id,
          ),
        ),
      ].map((id) =>
        [...accumulatedInterviews, ...recentInterviews].find(
          (interview) => interview.id === id,
        ),
      ),
    );
    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'INTERVIEW_SCHEDULE[]'.
  }, [recentInterviewData?.content]);

  const onLoadMore = React.useCallback(() => {
    if (isFetchingMore || !paginationState[selected.tabId]?.hasMorePages) {
      return;
    }

    setIsFetchingMore(true);
    const nextPage = paginationState[selected.tabId].pageNumber + 1;

    let fetchPromise;
    switch (selected.tabId) {
      case 'survey':
        fetchPromise = dispatch(
          getHmExperienceSurveys('PENDING', jobId, nextPage),
        );
        break;
      case 'applicationReview':
        fetchPromise = dispatch(
          getCandidateFeedbacks('PENDING', jobId, nextPage),
        );
        break;
      case 'interviewFeedbacks':
        fetchPromise = dispatch(
          getInterviewSchedules('PENDING', jobId, nextPage),
        );
        break;
      default:
        return;
    }

    fetchPromise
      // @ts-expect-error - TS2339 - Property 'then' does not exist on type 'ThunkAction<void>'. | TS7006 - Parameter 'result' implicitly has an 'any' type.
      .then((result) => result.payload || result)
      .then((response) => {
        setPaginationState((prev) => ({
          ...prev,
          [selected.tabId]: {
            pageNumber: nextPage,
            hasMorePages: !response?.last,
          },
        }));
        setIsFetchingMore(false);

        switch (selected.tabId) {
          case 'survey':
            // @ts-expect-error - TS2345 - Argument of type '(prev: never[]) => any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
            setAccumulatedSurveys((prev) => [
              ...prev,
              ...(response?.content || []),
            ]);
            break;
          case 'applicationReview':
            // @ts-expect-error - TS2345 - Argument of type '(prev: never[]) => any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
            setAccumulatedReviews((prev) => [
              ...prev,
              ...(response?.content || []),
            ]);
            break;
          case 'interviewFeedbacks':
            // Filter new interviews before adding them
            const newRecentInterviews = (response?.content || []).filter(
              // @ts-expect-error - TS7006 - Parameter 'interview' implicitly has an 'any' type.
              (interview) => {
                const interviewDateTime = new Date(interview.endDate);
                const currentDateTime = new Date();
                return interviewDateTime <= currentDateTime;
              },
            );
            // @ts-expect-error - TS2345 - Argument of type '(prev: never[]) => any[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
            setAccumulatedInterviews((prev) => [
              ...prev,
              ...newRecentInterviews,
            ]);
            break;
        }
      })
      .catch((error) => {
        setIsFetchingMore(false);
        console.error('Error loading more items:', error);
      });
  }, [dispatch, jobId, selected.tabId, isFetchingMore, paginationState]);

  // Reset pagination when tab changes
  React.useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      [selected.tabId]: {pageNumber: 0, hasMorePages: true},
    }));
    setIsFetchingMore(false);
  }, [selected.tabId]);

  const [containerRef, loaderRef, handleMovement] = useScrollLoader({
    loading: isFetchingMore,
    done: !paginationState[selected.tabId]?.hasMorePages,
    onLoadMore,
  });

  const handleRemoveSurvey = (surveyId: string) => {
    setAccumulatedSurveys(
      // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
      accumulatedSurveys.filter((survey) => survey.id !== surveyId),
    );
  };

  const renderContent = () => {
    switch (selected.tabId) {
      case 'applicationReview':
        return accumulatedReviews.length ? (
          <div
            // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            ref={containerRef}
            onScroll={handleMovement}
            className={css.contentContainer}
          >
            {accumulatedReviews.map((data, index) => (
              <ApplicationReviewCard
                key={index}
                applicantReviewData={data}
                jobId={jobId}
                from={from}
              />
            ))}
            {!paginationState[selected.tabId]?.hasMorePages ? null : (
              // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
              <div ref={loaderRef} className={css.loadingMoreContainer}>
                {isFetchingMore && <CircularLoader size="small" />}
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            title={t('No_Feedbacks_Yet', 'No Feedbacks Yet')}
            description={`${t(
              'All_clear_for_now_Keep_an_eye_out',
              'All clear for now. Keep an eye out',
            )}!`}
            imageVariant="file"
          />
        );
      case 'survey':
        return accumulatedSurveys.length ? (
          <div
            // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            ref={containerRef}
            onScroll={handleMovement}
            className={css.contentContainer}
          >
            {accumulatedSurveys.map((data, index) => (
              <SurveyCard
                // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'.
                key={data.id}
                surveyData={data}
                handleRemoveSurvey={handleRemoveSurvey}
              />
            ))}
            {!paginationState[selected.tabId]?.hasMorePages ? null : (
              // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
              <div ref={loaderRef} className={css.loadingMoreContainer}>
                {isFetchingMore && <CircularLoader size="small" />}
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            title={t('No_Feedbacks_Yet', 'No Feedbacks Yet')}
            description={`${t(
              'All_clear_for_now_Keep_an_eye_out',
              'All clear for now. Keep an eye out',
            )}!`}
            imageVariant="file"
          />
        );
      case 'interviewFeedbacks':
        return accumulatedInterviews.length ? (
          <div
            // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            ref={containerRef}
            onScroll={handleMovement}
            className={css.contentContainer}
          >
            {accumulatedInterviews.map((feedback, index) => (
              <InterviewFeedback
                key={index}
                feedbackData={feedback}
                from={from}
              />
            ))}
            {!paginationState[selected.tabId]?.hasMorePages ? null : (
              // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
              <div ref={loaderRef} className={css.loadingMoreContainer}>
                {isFetchingMore && <CircularLoader size="small" />}
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            title={t('No_Feedbacks_Yet', 'No Feedbacks Yet')}
            description={`${t(
              'All_clear_for_now_Keep_an_eye_out',
              'All clear for now. Keep an eye out',
            )}!`}
            imageVariant="file"
          />
        );
      case 'completed':
        const completedDataLength =
          (completedData as {content?: Array<INTERVIEW_SCHEDULE>})?.content
            ?.length || 0;
        const completedCandidateFeedbacksLength =
          (completedCandidateFeedbacks as {content?: Array<CANDIDATE_FEEDBACK>})
            ?.content?.length || 0;
        return completedDataLength > 0 ||
          completedCandidateFeedbacksLength > 0 ? (
          <CompletedFeedbackTab jobId={jobId} from={from} />
        ) : (
          <EmptyState
            title={t('No_Feedbacks_Yet', 'No Feedbacks Yet')}
            description={`${t(
              'All_clear_for_now_Keep_an_eye_out',
              'All clear for now. Keep an eye out',
            )}!`}
            imageVariant="file"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Panel
      anchor="right"
      initialFocus={1}
      onClose={onClose}
      size="large"
      isOpen={isOpen}
      tapOutsideToClose
      hideBackdrop={false}
    >
      <PanelHeader onCloseButtonClick={onClose}>
        <SubTitleLarge>{t('Feedbacks', 'Feedbacks')}</SubTitleLarge>
      </PanelHeader>
      <PanelBody className={css.panelBody}>
        <TabList
          onSelect={(tab) => {
            // @ts-expect-error - TS2345 - Argument of type '{ tabId: string; label?: string | undefined; }' is not assignable to parameter of type 'SetStateAction<{ tabId: TabId; label: string; }>'.
            setSelected(tab);
          }}
          selectedTab={{
            label: selected.label,
            tabId: selected.tabId,
          }}
          size="medium"
          menuWidth={sizeFluid}
        >
          {FEEDBACK_TABS.map(({label, tabId}) => (
            <Tab key={tabId} label={label} tabId={tabId} />
          ))}
        </TabList>
        {renderContent()}
      </PanelBody>
    </Panel>
  );
};

export default FeedbacksPanel;
